import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Button, { ButtonGroup } from 'components/buttons/Button';

import { Loader } from '../../../../components/Loader/Loader';
import { isToday } from 'utils/date';

import '../Widget.scss';
import './DeviationWidget.scss';
import { fetchDeviationsWithAuthor } from '../../../../fetch/deviationFetch';
import { DeviationTypes } from '../../../../types/orderTypes';
import { Order } from '../../../../types/orderTypes';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import Fetch from '../../../../utils/fetch';
import { settings } from '../../../../settings';

export default function CourierDeviationWidget() {
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);

  const [filterDay, setFilterDay] = useState<number>(0);
  const [collapsed, setCollapsed] = useState<boolean>(true);

  useEffect(() => {
    const fetchOrdersWithDeviations = async () => {
      const startDate = dayjs().add(-7, 'day').format('YYYY-MM-DD');
      const endDate = dayjs()
        .add(+15, 'day')
        .format('YYYY-MM-DD');

      try {
        const response = await Fetch.get({
          url: `${settings.newApiUrl}/order?start=${startDate}&end=${endDate}&status=active&deviations=${DeviationTypes.Courier}`,
        });
        const orderList: Order[] = response.ok ? await response.json() : [];
        setOrders(orderList);
      } catch (e) {
        console.warn('Error fetching orders', e);
        setOrders([]);
      }
      setIsLoading(false);
    };

    fetchOrdersWithDeviations();
  }, []);

  if (isLoading) {
    return (
      <div className="widget">
        <div className="widget__header">
          <h5>Courier Deviations today </h5>
        </div>
        <div className="widget__loader">
          <Loader type="spinner" size="large" />
        </div>
      </div>
    );
  }

  const timeToShowOrdersFor = dayjs()
    .add(filterDay, 'day')
    .format('YYYY-MM-DD');

  const ordersForGivenDay = orders.filter(
    order =>
      dayjs(order.deliverFrom).format('YYYY-MM-DD') === timeToShowOrdersFor
  );

  const changeDay = (changeInt: number) => {
    setCollapsed(false);
    setFilterDay(filterDay + changeInt);
  };

  return (
    <div className="widget">
      <div className="widget__header">
        <h5>Courier deviations</h5>
        {collapsed && (
          <span className="widget__expand">
            <span>Click to expand</span>
            <Button
              title={<FaAngleDown />}
              onClick={() => {
                setCollapsed(false);
              }}
            />
          </span>
        )}
        {!collapsed && (
          <span className="widget__expand">
            <span>Click to collapse</span>
            <Button title={<FaAngleUp />} onClick={() => setCollapsed(true)} />
          </span>
        )}
      </div>

      <CourierDeviationProblemOrders orders={orders} />

      <div style={{ height: 10 }} />

      <ButtonGroup>
        <Button title="<" onClick={() => changeDay(-1)} />
        <Button
          style={{ width: '100%' }}
          title={dayjs(timeToShowOrdersFor, 'YYYY-MM-DD').format(
            'dddd DD MMMM'
          )}
        />
        <Button
          title=">"
          onClick={() => changeDay(+1)}
          disabled={isToday(timeToShowOrdersFor)}
        />
      </ButtonGroup>
      <div style={{ width: '100%' }}>
        {!collapsed && (
          <table className="deviation-window-table">
            <thead>
              <tr>
                <th>OrderId</th>
                <th>Author</th>
                <th>Message</th>
                <th>Status</th>
                <th data-sortmethod="number">Time</th>
              </tr>
            </thead>

            <tbody>
              {ordersForGivenDay.map(order => (
                <CourierDeviationTableRows key={order.id} order={order} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const CourierDeviationTableRows = ({ order }: { order: Order }) => {
  const [courierDeviations, setCourierDeviations] = useState([]);

  useEffect(() => {
    fetchDeviationsWithAuthor(order.id)
      .then(deviations =>
        setCourierDeviations(
          deviations.filter(
            deviation => deviation.type === DeviationTypes.Courier
          )
        )
      )
      .catch(error => console.error(error.message));
  }, [order]);

  return (
    <>
      {courierDeviations.map((deviation, idx) => (
        <tr key={`${order.id}-${idx}`}>
          <td>
            <Link to={`/orders/${order.id}`} className="link__clean">
              {order.id}
            </Link>
          </td>
          <td>{deviation.authorName}</td>
          <td>{deviation.deviationMessage}</td>
          <td>{deviation.deviationStatus}</td>
          <td>{dayjs(deviation.timeOfDeviation).format('DD.MM HH.mm')}</td>
        </tr>
      ))}
    </>
  );
};

export function CourierDeviationProblemOrders({ orders }: { orders: Order[] }) {
  if (!orders.length) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div className="InfoWidgetBanner">
        <span>{<span>&#9888;</span>}</span>
        <span className="bannerBoxText">
          ORDERS WITH OPEN COURIER DEVIATION
        </span>
      </div>

      <div className="problem-box">
        <div className="list-order">
          {orders.map((item, index) => (
            <span key={index}>
              <Link
                className="problem-order-id"
                to={{ pathname: `/orders/${item.id}` }}
              >
                {item.id}
              </Link>
              {index < orders.length - 1 && <span>,&nbsp;</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
